/**
 * The files hung off one record: the list, the picker, the drop target and the
 * delete.
 *
 * ONE component for the project tab and the task card, not two, and that is a
 * decision worth defending rather than a shortcut. `CrmAttachment` is a single
 * polymorphic table with one set of routes over it, so a project's files and a
 * task's files are the same rows read with a different `entity`. Everything an
 * operator can do to one they can do to the other, the caps are the same caps,
 * and the refusals are the same three sentences. Two files here would be two
 * places for the "download by row id, never by storage key" rule to live, and
 * the copy that drifts is always the one nobody re-read. So the component is
 * named for what it is - the files of an entity - and lives in `_shared/` beside
 * `ActivityList`, which serves three screens for the same reason.
 *
 * What genuinely differs between the two owners is the WORDING, so the wording
 * is passed in: "no files on this project yet" and "no files on this task yet"
 * are different sentences about different things, and a component that picked
 * one would be deciding copy for screens it cannot see. The refusal sentences
 * are NOT passed in, because they are about the file rather than about its owner
 * ("that kind of file is not accepted here") and read identically wherever they
 * appear.
 *
 * The state machine behind the uploads is `useEntityFiles` next door. This file
 * is the card.
 *
 * ## The rule this whole design exists for
 *
 * There is no storage key in this file and there must never be one. The bucket
 * these objects live in is public-read - that is what lets tenant media load
 * without a signed URL - so a key rendered into the DOM would be an
 * unauthenticated download of a client's document, permanently, for anyone who
 * ever viewed the page. Downloads therefore point at `crmAttachmentUrl(id)`,
 * which is an API route gated on `view:crm` that looks the key up server-side.
 * If a change here seems to need a key in the browser, that is the bug, not the
 * missing feature.
 */
import { useRef, useState } from "react";
import { Download, FileText, Loader2, Trash2, Upload } from "lucide-react";
import { cn } from "@wa/ui";
import { useT } from "../../../i18n/I18nProvider";
import { userCanPlatform } from "../../../lib/auth";
import { crmAttachmentUrl, type CrmAttachment, type CrmAttachmentEntity } from "../../../lib/crmAttachments";
import { useCrmUser } from "../../shell/CrmUser";
import { Btn, Card, CardHead, EmptyState, IconBtn, fmtBytes, fmtDateTime, useToast } from "../../ui";
import { ConfirmModal } from "./ConfirmModal";
import { staffLabel, useStaff } from "./staff";
import { useEntityFiles } from "./useEntityFiles";

/**
 * A card body's padding. `crm-card` is a surface and nothing else, so anything
 * that is not a `crm-card-head` has to say where its edges are.
 */
const BODY = "px-6 pb-5 pt-1";

export function EntityFiles({
  entity,
  entityId,
  title,
  emptyBody,
  className,
  onChange,
}: {
  entity: CrmAttachmentEntity;
  entityId: string;
  /** The card's heading, already translated. */
  title: string;
  /** What the empty card says, already translated. See the header. */
  emptyBody: string;
  /** For the caller's layout - the task page's weave order, for instance. */
  className?: string;
  /** Tells the page frame that a counter outside this card is now stale. */
  onChange?: () => void;
}) {
  const t = useT();
  const toast = useToast();
  const { byId } = useStaff();
  // The same right the API gates the write routes on. Both owners are behind
  // `manage:crm.projects` there, so one check covers the card wherever it is
  // drawn - a viewer sees the list and no way to change it.
  const canManage = userCanPlatform(useCrmUser(), "manage:crm.projects");

  const files = useEntityFiles({
    entity,
    entityId,
    onReadFailed: () => toast(t("common.error")),
    // The hook hands over the KEY of the sentence rather than the failure
    // itself, for the same reason it hands over keys for a refused upload: it
    // is a state machine with no `t` and no business holding one. The reason
    // there is a key to hand over at all is that one delete failure has a
    // remedy and the rest do not - the storage refusal is the server keeping
    // the row so a second press can finish the job, and "Something went wrong."
    // beside a row that did not move sends nobody to press anything.
    onWriteFailed: (messageKey) => toast(t(messageKey)),
    onChange,
  });
  const { rows, limits, pending } = files;

  const [dragOver, setDragOver] = useState(false);
  const [confirmDel, setConfirmDel] = useState<CrmAttachment | null>(null);
  const input = useRef<HTMLInputElement>(null);

  const uploadBtn = canManage ? (
    <Btn small tone="dark" disabled={files.uploading || files.full} onClick={() => input.current?.click()}>
      {files.uploading ? <Loader2 className="size-4 animate-spin" /> : <Upload className="size-4" />}
      {t(files.uploading ? "projects.uploading" : "projects.upload")}
    </Btn>
  ) : null;

  return (
    <>
      <Card
        className={cn(dragOver && "ring-2 ring-primary/60", className)}
        // The whole card is the drop target, not a strip inside it: a drag
        // carrying a file has to be released somewhere obvious, and the obvious
        // place is the thing the file is going into. `preventDefault` on
        // `dragover` is what stops the browser from navigating away to open the
        // file instead - without it, dropping a PDF on this card replaces the CRM
        // with a PDF viewer.
        onDragOver={
          canManage
            ? (e) => {
                e.preventDefault();
                setDragOver(true);
              }
            : undefined
        }
        onDragLeave={canManage ? () => setDragOver(false) : undefined}
        onDrop={
          canManage
            ? (e) => {
                e.preventDefault();
                setDragOver(false);
                if (e.dataTransfer.files.length) files.add(e.dataTransfer.files);
              }
            : undefined
        }
      >
        <CardHead
          title={title}
          /**
           * How many files, and the biggest one that will be accepted.
           *
           * The cap is a bare figure rather than a sentence because there is no
           * catalogue entry for one, and inventing English copy in a component
           * would be a hardcoded string in a product that ships in two
           * languages. A formatted size reads the same in both - `fmtBytes`
           * already runs through the Estonian locale - and the relation sign
           * beside a picker needs no verb in either. It is drawn from the
           * server's answer and never from a local constant: the whole point of
           * serving the limits is that only one number exists.
           */
          sub={
            <span className="crm-tnum">
              {rows && rows.length > 0 ? t("projects.fileCount", { n: rows.length }) : null}
              {rows && rows.length > 0 && limits ? " · " : null}
              {limits ? `≤ ${fmtBytes(limits.maxBytes)}` : null}
            </span>
          }
          actions={uploadBtn}
        />

        <div className={BODY}>
          {/* One input for the button and nothing else - the drop path never
              touches it. Cleared after every pick so choosing the SAME file
              again still fires `change`, which is what makes "it failed, try
              that one again" work at all. */}
          <input
            ref={input}
            type="file"
            multiple
            className="hidden"
            onChange={(e) => {
              files.add(e.target.files);
              e.target.value = "";
            }}
          />

          {rows === null ? (
            <div className="flex justify-center py-12 text-muted-foreground">
              <Loader2 className="size-6 animate-spin" />
            </div>
          ) : rows.length === 0 && pending.length === 0 ? (
            <EmptyState icon={FileText} title={title} body={emptyBody} action={uploadBtn} />
          ) : (
            <ul className="flex flex-col">
              {rows.map((file) => (
                <FileRow
                  key={file.id}
                  file={file}
                  uploader={file.uploadedBy ? staffLabel(byId, file.uploadedBy) : null}
                  onRemove={canManage ? () => setConfirmDel(file) : undefined}
                />
              ))}
              {/* Under the stored rows rather than over them: an upload in
                  flight is the newest file, and the list is oldest first. */}
              {pending.map((p) => (
                <li
                  key={p.localId}
                  className="flex items-center gap-3 border-t border-[var(--crm-hair)] py-2.5 text-[13px] text-muted-foreground first:border-t-0"
                >
                  <Loader2 className="size-4 flex-none animate-spin" />
                  <span className="crm-trunc min-w-0 flex-1">{p.name}</span>
                  <span className="crm-tnum flex-none text-[12px]">{fmtBytes(p.size)}</span>
                </li>
              ))}
            </ul>
          )}

          {/* Drawn, and left standing until the next pick. A refusal here is a
              sentence that says what to do instead, and a toast is gone in 2.8
              seconds - not long enough to read one, let alone two when a pick of
              five files was refused for two different reasons. */}
          {files.refusals.length > 0 ? (
            <div className="mt-3 flex flex-col gap-1.5">
              {files.refusals.map((key) => (
                <p key={key} className="text-[12.5px] leading-relaxed text-red-600 dark:text-red-300">
                  {t(key)}
                </p>
              ))}
            </div>
          ) : null}
        </div>
      </Card>

      {/* The file's own name as the body, because that is the only thing that
          tells this delete from the one under the row above it, and it is data
          rather than copy - nothing to translate, nothing to get wrong in one
          language. The bytes go with the row and there is no undo, which is why
          this asks at all. */}
      {confirmDel ? (
        <ConfirmModal
          title={t("projects.removeFile")}
          body={confirmDel.name}
          confirmLabel={t("common.delete")}
          cancelLabel={t("common.cancel")}
          onConfirm={async () => {
            const file = confirmDel;
            setConfirmDel(null);
            await files.remove(file);
          }}
          onClose={() => setConfirmDel(null)}
        />
      ) : null}
    </>
  );
}

/**
 * One stored file.
 *
 * Two lines rather than a column grid, and that is what lets the same row serve
 * a full-width project tab and a 380px card in the task page's right-hand
 * column. A five-column table would need a second layout for the narrow case,
 * and a second layout is a second set of truncation bugs.
 */
function FileRow({
  file,
  uploader,
  onRemove,
}: {
  file: CrmAttachment;
  /** The uploader's name, or null on a file that was imported rather than uploaded. */
  uploader: string | null;
  /** Absent for a reader: no button rather than a disabled one. */
  onRemove?: () => void;
}) {
  const t = useT();

  return (
    <li className="flex items-center gap-3 border-t border-[var(--crm-hair)] py-2.5 first:border-t-0">
      <span className="crm-av crm-av-soft flex-none" aria-hidden>
        <FileText className="size-4" />
      </span>

      {/* `min-w-0` is what lets a long filename truncate instead of pushing the
          two buttons out of a narrow card. */}
      <span className="min-w-0 flex-1">
        <span className="crm-prow-n crm-trunc block">{file.name}</span>
        <span className="crm-prow-s crm-trunc block">
          <span className="crm-tnum">{fmtBytes(file.sizeBytes)}</span>
          {/* The uploader is dropped rather than marked when there is none. A row
              with no uploader is an imported file, and "?" beside it would claim
              we lost the name of somebody who never existed here. */}
          {uploader ? ` · ${uploader}` : null}
          {" · "}
          <span className="crm-tnum">{fmtDateTime(file.createdAt)}</span>
        </span>
      </span>

      {/* A real link, so the browser's own save dialog, progress and retry do the
          work. The href is an API route that resolves the key server-side behind
          `view:crm` - there is no CDN address for these files, by design. */}
      <a
        href={crmAttachmentUrl(file.id)}
        download={file.name}
        className="crm-cb crm-cb-sm flex-none"
        title={t("projects.download")}
        aria-label={t("projects.download")}
      >
        <Download className="size-4" />
      </a>

      {onRemove ? (
        <IconBtn
          small
          title={t("projects.removeFile")}
          aria-label={t("projects.removeFile")}
          onClick={onRemove}
        >
          <Trash2 className="size-4" />
        </IconBtn>
      ) : null}
    </li>
  );
}
